/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {useEffect, useState} from 'react';
import {SophiaBannerItem} from '../containers/AppHome/types';
import SophiaService, {SophiaLocaleMap} from '../services/SophiaService';
import {fakeIllustratorBannerData} from '../containers/AppHome/appHomeData';

// Reference for this paradigm: https://levelup.gitconnected.com/react-hooks-gotchas-setstate-in-async-effects-d2fd84b02305
type PromiseState =
	| { status: 'idle' | 'pending'; value: null; error: null }
	| { status: 'fulfilled'; value: SophiaBannerItem; error: null }
	| { status: 'rejected'; value: null; error: Error };

/**
 * Method to fetch data for category page.
 *
 */
export default function FetchAppHomeBannerData(appContextType: string, locale: string) {
	const [appHomesBannerState, setappHomesBannerState] = useState<PromiseState>({
		status: 'pending',
		value: null,
		error: null,
	});

	useEffect(() => {
		const appHomesBannerResponse = async() => {
			let surfaceId;
			if (appContextType === 'photoshopweb') {
				surfaceId = 'CCW_HOME_PS_BANNERS';
			}
			else if (appContextType === 'canvas') {
				surfaceId = 'CCW_HOME_CANVAS_BANNERS';
			}
			else if (appContextType === 'illustratorweb' && window.cchome.util.hasFeature('ai_web_data')) {
				setappHomesBannerState({
					status: 'fulfilled',
					value: fakeIllustratorBannerData.fakeBanner,
					error: null,
				});
				return;
			}
			const response = await SophiaService.fetchSurfaceDataFromSophia(
				surfaceId,
				SophiaLocaleMap[locale],
				appContextType,
			);

			if (response instanceof Error) {
				setappHomesBannerState({
					status: 'rejected',
					value: null,
					error: response,
				});
			}
			else {

				try {
					const container = response.containers && response.containers[0];
					const parsedContainer = JSON.parse(container.data);

					const parsedData = parsedContainer.data;
					const firstKey = Object.keys(parsedData)[0];
					let parsedBannerData;
					if (parsedData[firstKey] && parsedData[firstKey].item) {
						parsedBannerData = parsedData[firstKey].item;
					}
					const bannerInfo = parsedBannerData.itemList[0];

					// as agreed w/ Tondo, this is the unique id for the banner
					const bannerId = bannerInfo.ingestDescription;
					const trackingCategory = 'web-apphomes-banner';

					// Get tracking of the banner.
					const trackedBanner = await window.trackingService.getCompletionStatus({
						resource: bannerId,
						category: trackingCategory,
					});
					window.cchome.util.logger.info('FetchAppHomeBannerData', 'Banner track response', trackedBanner);
					if (trackedBanner && trackedBanner.status) {
						if (trackedBanner.status === 'complete') {
							bannerInfo.isOpen = false;
						}
						else {
							bannerInfo.isOpen = true;
						}
					}

					setappHomesBannerState({
						status: 'fulfilled',
						value: bannerInfo,
						error: null,
					});
				}
				catch (e) {
					setappHomesBannerState({
						status: 'rejected',
						value: null,
						error: new Error('Failed to parse Sophia Banner data'),
					});
				}

			}
		};
		if (!appHomesBannerState.value) {
			appHomesBannerResponse();
		}
	}, [appContextType]);

	return appHomesBannerState;
}
